import React, { useEffect, useState } from 'react';
import { Button, Drawer, ToggleSwitch, FormCheckbox } from 'components/common';
import { Controller, useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { MdOutlineClose, MdOutlineSave } from 'react-icons/md';
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import Select from 'react-select'
import AsyncSelect from 'react-select/async';
import { updateRequestUserAccount } from "redux/request/userAccountSlice";
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { getDepartmentBySite } from "redux/organization/siteSlice";
import { activeEmployeesDebounce } from "helpers/employeeOptionHelper";
import { isValidPhoneNumber } from "libphonenumber-js"
import PhoneInput from 'react-phone-input-2';
import { REQUEST_USER_ACCOUNT_PURPOSE } from "app_config";
import { FaInfoCircle } from 'react-icons/fa';

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const EditRequestUserAccountForm = ({ open, setOpen, record }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    defaultValues: {
      purpose: []
    },
    mode: 'onChange',
  });

  const { data: user } = useSelector(state => state.profile_user);
  const [countryCode, setCountryCode] = useState('VN');
  const siteId = user && user.site ? user.site.id : null;
  const dispatch = useDispatch();

  useEffect(() => {
    setValue("remind_to_it", true);

    if (record) {
      Object.entries(record).forEach(([key, value]) => {
        switch (key) {
          case "onboard_date":
            setValue(key, value ? new Date(value) : null);
            break;
          case "purpose":
            setValue(key, Array.isArray(value) ? value : []);
            break;
          case "department":
            setValue("department_id", value ? value.id : null);
            break;
          default:
            setValue(key, value);
            break;
        }
      })
    } else {
      reset({ purpose: [] });
    }
  }, [setValue, record, open, reset]);

  const [departmentOption, setDepartmentOption] = useState([]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await dispatch(getDepartmentBySite(siteId)).unwrap();

        const options = response.rows.map(item => item.attributes).map(item => ({
          value: item.id,
          label: item.name
        }));

        setDepartmentOption(options);

      } catch (error) {
        toast.error(error.message);
      }
    }

    fetchDepartments();
  }, [dispatch, siteId]);

  const loadManagerOptions = (inputValue, callback) => {
    activeEmployeesDebounce(inputValue, callback);
  };


  const validatePhoneNumber = (number) => {
    if (number === undefined || number === "" || number === null) return undefined

    if (!isValidPhoneNumber(number.toString(), countryCode)) {
      return `Invalid phone number with country code ${countryCode}.`;
    }
    return true;
  }

  const closeModal = () => {
    setOpen(false);
  }

  const onSubmit = async (data) => {
    try {
      await dispatch(updateRequestUserAccount({ recordId: record?.id, data }));
      closeModal();
    }
    catch (error) {
      toast.error(error.message)
      return;
    }
  }

  const fieldRow = (label, children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
      <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-text-right">
        {label}
      </div>
      <div className="tw-col-span-12 sm:tw-col-span-7">
        {children}
      </div>
    </div>
  );

  const fieldError = (error) => error && (
    <span className="tw-text-red-600 tw-text-xs tw-mt-1 tw-block">{error.message}</span>
  );

  return (
    <Drawer
      show={open}
      onHide={closeModal}
      title="Edit Request User Account"
      width="50%"
      backdropClick={false}
      footer={(
        <>
          <Button size="sm" type="submit" variant="primary" className="tw-mr-3" form="edit-request-user-account-form">
            <MdOutlineSave /> Update
          </Button>
          <Button size="sm" variant="danger" onClick={closeModal}>
            <MdOutlineClose /> Close
          </Button>
        </>
      )}
    >
      <form id="edit-request-user-account-form" onSubmit={handleSubmit(onSubmit)}>
        {fieldRow('Full of name*', (
          <div className="tw-grid tw-grid-cols-2 tw-gap-1">
            <div>
              <input
                type='text'
                placeholder='First name'
                className={INPUT_CLASSES}
                {...register('first_name', {
                  required: 'First name must be required.'
                })}
              />
              {fieldError(errors.first_name)}
            </div>
            <div>
              <input
                type='text'
                placeholder='Last name'
                className={INPUT_CLASSES}
                {...register('last_name', {
                  required: 'Last name must be required.'
                })}
              />
              {fieldError(errors.last_name)}
            </div>
          </div>
        ))}

        {fieldRow('Code', (
          <>
            <input
              type='text'
              placeholder='3 characters as policy'
              className={INPUT_CLASSES}
              {...register('name')}
            />
            {fieldError(errors.name)}
          </>
        ))}

        {fieldRow('Title*', (
          <>
            <input
              type='text'
              placeholder='Title'
              className={INPUT_CLASSES}
              {...register('title', {
                required: 'Title must be required.'
              })}
            />
            {fieldError(errors.title)}
          </>
        ))}

        {fieldRow('Phone number*', (
          <>
            <Controller
              control={control}
              name="phone_number"
              rules={{ validate: validatePhoneNumber }}
              render={({ field: { ref, ...field } }) => (
                <PhoneInput
                  {...field}
                  inputProps={{ ref }}
                  country='vn'
                  autoFormat={true}
                  countryCodeEditable={false}
                  enableSearch={true}
                  disableSearchIcon={true}
                  inputStyle={{ width: '100%' }}
                  searchStyle={{ width: '95%' }}
                  onChange={(value, country) => {
                    field.onChange(value);
                    setCountryCode(country.countryCode.toUpperCase());
                  }}
                />
              )}
            />
            {fieldError(errors.phone_number)}
          </>
        ))}

        {fieldRow('Gender*', (
          <>
            <Controller
              control={control}
              name='gender'
              defaultValue={false}
              rules={{ required: 'Gender must be required.' }}
              render={({ field }) => (
                <div className="tw-flex tw-flex-row tw-gap-4">
                  {["Male", "Female"].map(item => (
                    <label key={item} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-cursor-pointer">
                      <input
                        type="radio"
                        className="tw-cursor-pointer"
                        checked={field.value === item}
                        onChange={() => setValue('gender', item)}
                      />
                      {item}
                    </label>
                  ))}
                </div>
              )}
            />
            {fieldError(errors.gender)}
          </>
        ))}

        {fieldRow('Onboard date', (
          <Controller
            control={control}
            name="onboard_date"
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onChange={d => field.onChange(adjustToNoon(d))}
                className={INPUT_CLASSES}
                placeholderText="DD-MM-YYYY"
                dateFormat="dd-MM-yyyy"
                fixedHeight
                isClearable={true}
              />
            )}
          />
        ))}

        {fieldRow('Line Manager', (
          <>
            <Controller
              control={control}
              name="manager"
              render={({ field }) => (
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadManagerOptions}
                  closeMenuOnSelect={true}
                  placeholder='<Select manager>'
                  classNamePrefix="react-select"
                  className="tw-w-full"
                  value={field.value ? { value: field.value.id, label: field.value.name } : null}
                  onChange={selectedOption => field.onChange(selectedOption ? { id: selectedOption.value, name: selectedOption.label } : null)}
                />
              )}
            />
            {fieldError(errors.manager)}
          </>
        ))}

        {fieldRow('Deparment', (
          <Controller
            control={control}
            name='department_id'
            render={({ field }) => (
              <Select
                isClearable
                options={departmentOption}
                value={departmentOption.length > 0 ? departmentOption.find(opt => opt.value === field.value) : null}
                onChange={selectedOption => {
                  field.onChange(selectedOption ? selectedOption.value : null);
                }}
                placeholder='Select...'
                classNamePrefix='react-select'
                className='tw-w-full'
              />
            )}
          />
        ))}

        {fieldRow('Purpose*', (
          <>
            <Controller
              control={control}
              name='purpose'
              rules={{
                validate: value => (value && value.length > 0) || 'Purpose must be required.'
              }}
              render={({ field }) => (
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  {REQUEST_USER_ACCOUNT_PURPOSE.map(item => (
                    <FormCheckbox
                      key={item}
                      label={item}
                      checked={Array.isArray(field.value) && field.value.includes(item)}
                      onChange={() => {
                        const currentValue = Array.isArray(field.value) ? field.value : [];
                        if (currentValue.includes(item)) {
                          field.onChange(currentValue.filter(i => i !== item));
                        } else {
                          field.onChange([...currentValue, item]);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            />
            {fieldError(errors.purpose)}
          </>
        ))}

        {fieldRow('Remark', (
          <textarea
            rows={10}
            placeholder="Give some comments..."
            className={INPUT_CLASSES}
            {...register("remark")}
          />
        ))}

        {fieldRow('', (
          <>
            <Controller
              control={control}
              name="remind_to_it"
              render={({ field }) => (
                <ToggleSwitch
                  value={!!field.value}
                  onChange={field.onChange}
                  label="Remind IT department"
                />
              )}
            />
            <span className="tw-text-red-600 tw-text-xs tw-inline-flex tw-items-center tw-gap-1">
              <FaInfoCircle /> Remind some updates to IT department by email.
            </span>
          </>
        ))}
      </form>
    </Drawer>
  )
}

EditRequestUserAccountForm.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  record: PropTypes.object
};

export default EditRequestUserAccountForm;
